import { AnimatePresence } from 'framer-motion';

import { Slide } from '@components/animators/slide';
import { Button } from '@components/button';
import { DesignAvatar } from '@components/design-avatar';
import { FormError } from '@components/form-error';
import { useConfirmModal } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArrowDown } from '@components/icons/arrow-down';
import { IconAskAI } from '@components/icons/ask-ai';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconInfoSmall } from '@components/icons/info-small';
import { IconOutput } from '@components/icons/output';
import { IconPlay } from '@components/icons/play';
import { IconTextLong } from '@components/icons/text-long';
import { InfoBox } from '@components/info-box';
import { JsonValue } from '@components/json-editor';
import { forceHideSidebar, InfoButton, Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useAIAgent } from 'api/ai-agents/hooks';
import { createJourneyActionFieldPath } from 'api/documents/requests';
import { useUploadFile } from 'api/files/hooks';
import { useWebSocket } from 'api/hooks';
import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { AIModel, AIReasoningEffort, JourneyActionDetailed, VariableValue } from 'api/types';
import { ACTION_TYPES } from 'ui/actions/action-config';
import {
  aiModelOptions,
  Output,
  Property,
  reasoningEffortsOptions,
} from 'ui/authenticated/main/pages/ai-agents/agent-details-sidebar';
import { useFeature } from 'ui/hooks';
import {
  ActionFooter,
  ActionHeader,
  ActionSidebarLoading,
  createActionForm,
  DescriptionFormField,
  SidebarProps,
  useCreateActionFormStore,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';

import { useAIAgentTestModal } from './test-modal';

import { useOutputModal } from '../../authenticated/main/pages/ai-agents/output-modal';
import { FormWithUnsavedChangesPrompt } from '../../journey-builder/unsaved-changes-prompt';
import { getFieldMappingId } from '../helpers';

const ACTION_TYPE = ACTION_TYPES.ask_ai_agent;

export type FormData = {
  description: string;
  prompt: VariableValue;
  additionalFiles: Array<VariableValue>;
  temperature: number;
  reasoningEffort?: AIReasoningEffort;
  systemInstructions: string;
  model: AIModel;
  jsonSchema?: JsonValue;
  output: Property[];
  hasStructuredOutput: boolean;
  useWebSearch: boolean;
  useFileSearch: boolean;
};

const Form = createActionForm<FormData>();

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<typeof ACTION_TYPE>(journeyActionId);

  if (isLoading || !data) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  return (
    <Sidebar>
      <ActionHeader
        actionType={ACTION_TYPE}
        title={data.values?.name || undefined}
        icon={() => <DesignAvatar src={data.values?.picture} />}
        {...props}
      >
        <InfoButton>
          <Info />
        </InfoButton>
      </ActionHeader>
      <Sidebar.Content>
        <AnimatePresence initial={false} exitBeforeEnter>
          <Slide key="form" offset={-30}>
            <FormContainer journeyActionId={journeyActionId} data={data} />
          </Slide>
        </AnimatePresence>
      </Sidebar.Content>
    </Sidebar>
  );
};

const Info = () => {
  const { t } = useScopedTranslate(sidebarLocale);
  return (
    <Stack space={4}>
      <Text font="bold16" text={t('infoTitle')} />
      <Text font="regular14" text={t('infoText')} />
      <Stack space={2} align="center">
        <InfoBox variant="blue" icon={<IconTextLong />} text={t('infoPrompt')} />
        <IconArrowDown color="elementPurple50" />
        <div style={{ width: '100%' }}>
          <InfoBox variant="green" icon={<IconCheckmark />} text={t('infoSelectOutput')} />
        </div>
        <IconArrowDown color="elementPurple50" />
        <InfoBox variant="blue" icon={<IconAskAI />} text={t('infoAnalyze')} />
        <IconArrowDown color="elementPurple50" />
        <InfoBox variant="indigo" icon={<IconOutput />} text={t('infoOutput')} />
      </Stack>
    </Stack>
  );
};

const FormContainer = ({
  journeyActionId,
  data,
}: {
  journeyActionId: string;
  data: JourneyActionDetailed<'ask_ai_agent'>;
}) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { isEnabled: hasContentAIEnabled } = useFeature('FEATURE_AI_CONTENT');
  const formStore = useCreateActionFormStore();
  const output = (formStore.useFieldValue('output') as Property[]) ?? data.values?.output;
  const model = formStore.useFieldValue('model') as AIModel;
  const formDescription = formStore.useFieldValue('description');
  const hasStructuredOutput =
    (formStore.useFieldValue('hasStructuredOutput') as boolean) ?? data.values?.hasStructuredOutput;
  const showConfirmModal = useConfirmModal();
  const additionalFiles =
    (formStore.useFieldValue('additionalFiles') as Array<VariableValue>) ??
    data.values?.additionalFiles;
  const variables =
    ((formStore.useFieldValue('prompt') as VariableValue) ?? data.values?.prompt ?? {})[
      'variables'
    ] ?? {};
  const isDirty = formStore.useIsDirty();
  const editMutation = useUpdateJourneyAction(journeyActionId, { onSuccess: () => false });
  const showOutputModal = useOutputModal();
  const showTestModal = useAIAgentTestModal();

  const handleOutputModal = (index?: number) => {
    showOutputModal({
      setValue: (value) => formStore.actions.setValue('output', value),
      getValue: () => formStore.getState().values['output'] as Property[],
      currentIndex: index,
    });
  };

  const handleSave = async (formData: FormData, shouldHide: boolean = true) => {
    const newValues = {
      ...data.values,
      ...formData,
    };
    await editMutation.mutateAsync({
      description: formData.description,
      isAutomatic: true,
      action: ACTION_TYPE,
      values: { ...newValues, jsonSchema: newValues.jsonSchema ?? undefined },
    });
    if (shouldHide) {
      forceHideSidebar();
    }
  };

  const variableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    excludedTypes: ['file', 'file_multi'],
  });

  const fileGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    types: ['file', 'file_multi'],
  });

  const handleDeleteOutput = (index: number) => {
    const state = formStore.getState();
    const currentOutput = (state.values['output'] ?? []) as Property[];
    formStore.actions.setValue(
      'output',
      currentOutput.filter((_, i) => i !== index)
    );
  };

  const uploadMutation = useUploadFile(
    createJourneyActionFieldPath(data?.journey ?? '', journeyActionId),
    true
  );

  // Fetch template to check vector store status
  const aiAgentId = data.values?.aiAgentId ?? '';
  const hasTemplateFiles = data.values?.files && data.values.files.length > 0;
  const { data: aiAgentTemplate } = useAIAgent(aiAgentId, { enabled: Boolean(hasTemplateFiles) });
  const isVectorStoreIndexing = aiAgentTemplate?.vectorStoreStatus === 'indexing';
  const isVectorStoreFailed = aiAgentTemplate?.vectorStoreStatus === 'failed';

  const handleShowTestModal = () => {
    showTestModal({
      actionId: journeyActionId,
      variables,
      additionalFiles,
      outputSettings: hasStructuredOutput
        ? output
        : [{ name: '__text', label: t('text'), type: 'paragraph', instructions: '' }],
      onFileSelect: uploadMutation.mutateAsync,
    });
  };

  const handleTestClick = () => {
    if (isDirty) {
      showConfirmModal({
        title: t('unsavedChanges'),
        description: t('unsavedChangesDescription'),
        onConfirm: async () => {
          await handleSave(formStore.getState().values as FormData, false);
          setTimeout(handleShowTestModal);
        },
        buttonText: t('core.save'),
      });
    } else {
      handleShowTestModal();
    }
  };
  const hasOutputError = formStore.useFormErrors().output;
  const actionDescription = formDescription || data.description;
  const fieldMappingId = getFieldMappingId(data.journey ?? '');
  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={(data) => handleSave(data)}>
      <Stack space={6}>
        {isVectorStoreFailed && (
          <InfoBox
            variant="red"
            icon={<IconFormIndicatorWarning />}
            text={t('vectorStoreFailedWarning')}
          />
        )}
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={data.description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('input')}>
          <Stack space={4}>
            <Form.FieldMappingTextEditor
              id="prompt"
              label={t('prompt')}
              variableGroups={variableGroups}
              defaultValue={data.values?.prompt}
              plaintext
              validators={['required']}
              fieldMappingId={fieldMappingId}
              aiOptions={
                hasContentAIEnabled
                  ? {
                      generateChoices: actionDescription
                        ? [
                            {
                              title: t('generateFromActionDescription'),
                              prompt: `Write an AI agent prompt with this description: ${actionDescription}`,
                            },
                          ]
                        : [],
                      threadInstructions:
                        'Transform the users input into a refined prompt intended for another AI agent. Do not generate any output beyond the prompt itself—do not write the actual story, joke, or content. Only return the improved prompt. For instance, if the user says "write a story about a robot", the refined prompt should be: "You are a creative storyteller. Write a captivating short story about a curious robot exploring a futuristic world."',
                      useWebSocket,
                    }
                  : undefined
              }
            />
            <Form.FieldMappingFileListField
              id="additionalFiles"
              label={t('additionalFiles')}
              variableGroups={fileGroups}
              defaultValue={data.values?.additionalFiles}
              onSelect={uploadMutation.mutateAsync}
              fieldMappingId={fieldMappingId}
            />
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('output')}>
          <Stack space={4}>
            <Form.Switch
              id="hasStructuredOutput"
              label={t('hasStructuredOutput')}
              defaultValue={data.values?.hasStructuredOutput}
            />
            {hasStructuredOutput && (
              <>
                {hasOutputError && <FormError text={t('outputRequired')} />}
                <Stack space={2}>
                  <Output
                    items={output}
                    handleOutputModal={handleOutputModal}
                    handleDeleteOutput={handleDeleteOutput}
                    setValue={(value) => formStore.actions.setValue('output', value)}
                  />
                  <Stack orientation="horizontal">
                    <Form.HiddenField
                      id="output"
                      validators={['required']}
                      defaultValue={data.values?.output}
                    />
                    <Button
                      variant="secondary"
                      text={t('addOutput')}
                      onClick={() => handleOutputModal()}
                      iconLeft={<IconAddPlus />}
                    />
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('advancedSettings')} defaultClosed>
          <Stack space={4}>
            <Form.Textarea
              id="systemInstructions"
              label={t('systemInstructions')}
              defaultValue={data.values?.systemInstructions}
              validators={['required']}
              isExpandable
            />
            <Form.Select
              id="model"
              label={t('model')}
              defaultValue={data.values?.model}
              options={aiModelOptions}
              validators={['required']}
              overflowWidth
            />
            {model?.startsWith('gpt') && (
              <Form.Slider
                // @ts-ignore-next-line id is whining -\_(ツ)_/-
                id="temperature"
                label={t('temperature')}
                hideLabel={false}
                step={0.01}
                maxValue={2}
                defaultValue={data.values?.temperature ?? 1}
                info={t('temperatureInfo')}
              />
            )}
            {model?.startsWith('o') && (
              <Form.Select
                id="reasoningEffort"
                label={t('reasoningEffort')}
                defaultValue={data.values?.reasoningEffort ?? 'medium'}
                options={reasoningEffortsOptions}
                validators={['required']}
                isClearable={false}
              />
            )}
            <Stack orientation="horizontal" space={2} align="center">
              <Form.Switch
                id="useWebSearch"
                label={t('useWebSearch')}
                defaultValue={data.values?.useWebSearch ?? false}
              />
              <IconInfoSmall color="purple50" tooltip={t('useWebSearchTooltip')} />
            </Stack>
            <Stack orientation="horizontal" space={2} align="center">
              <Form.Switch
                id="useFileSearch"
                label={t('useFileSearch')}
                defaultValue={data.values?.useFileSearch ?? false}
              />
              <IconInfoSmall color="purple50" tooltip={t('useFileSearchTooltip')} />
            </Stack>
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
      </Stack>
      <ActionFooter action={data} isDisabled={!isDirty || editMutation.isLoading} form={Form}>
        <Button
          variant="ghost"
          iconLeft={<IconPlay />}
          text={t('test')}
          onClick={handleTestClick}
          isDisabled={isVectorStoreIndexing}
          tooltip={isVectorStoreIndexing ? t('testDisabledIndexing') : undefined}
        />
      </ActionFooter>
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    actionDetails: 'Action details',
    advancedSettings: 'Advanced settings',
    systemInstructions: 'System instructions',
    model: 'Model',
    input: 'Input',
    output: 'Output',
    outputRequired: 'You need to add at least one output',
    prompt: 'Prompt',
    additionalFiles: 'Additional files',
    text: 'Text',
    addOutput: 'Add',
    hasStructuredOutput: 'Use structured output',
    temperature: 'Temperature',
    temperatureInfo:
      'The temperature parameter controls the randomness of the model. Lower values make the model more deterministic, while higher values make it more creative.',
    reasoningEffort: 'Reasoning effort',
    template: 'Prompt templates',
    selectTemplate: 'Select template',
    field: 'Field',
    fieldInfo: 'Field to apply the prompt to',
    infoTitle: 'About Ask AI Agent',
    infoText:
      'Ask AI Agent is a tool that helps you analyse and process data using AI with predefined system instructions',
    infoPrompt:
      'Create the prompt for AI, you can input text or use tokens from the journey/traveller',
    infoSelectOutput: 'Add output fields, with instructions for the AI on how to process the data',
    infoAnalyze: 'AI Analyses the input based on your specifications and guidelines',
    infoOutput:
      'AI outputs the data in the format you specified, you can use this data to further process or analyse',
    test: 'Test',
    testDisabledIndexing: 'Cannot test while knowledge base is indexing',
    vectorStoreFailedWarning:
      'Knowledge base indexing failed. Please re-save the AI Agent template to retry.',
    unsavedChanges: 'Unsaved changes',
    unsavedChangesDescription: 'Before testing, please save your changes',
    generateFromActionDescription: 'Generate from action description',
    useWebSearch: 'Use web search',
    useWebSearchTooltip: 'Enable to allow the AI to search the web for up-to-date information',
    useFileSearch: 'Use file search for additional files',
    useFileSearchTooltip:
      'Enable for large documents or when searching for specific information. Disable for small files where the AI should read the complete content.',
  },
};
